<template>
  <div class="hero-slide triplet">
    <template v-if="(simple.length < 1 || detail.length < 2)">
      <StartpageHeroTemplateBanner :entries="entries" />
    </template>
    <div v-else class="grid grid-cols-7 gap-5">
      <template v-if="simple.length == 1">
        <div class="entry col-span-3">
          <StartpageHeroElementTileSimple :entry="simple[0]" class="h-[500px]" />
        </div>
      </template>
      <template v-else>
        <div class="col-span-3 carousel-ctrl-top-right">
          <NuxtCarousel v-bind="carouselPrimaryConfig">
            <NuxtSlide v-for="entry in simple" :key="entry.priority">
              <BasicBtErrorBoundary>
                <StartpageHeroElementTileSimple :entry="entry" class="h-full justify-center" />
              </BasicBtErrorBoundary>
            </NuxtSlide>
            <template #addons>
              <NuxtNavigation />
            </template>
          </NuxtCarousel>
        </div>
      </template>
      <template v-if="detail.length == 2">
        <div class="grid grid-cols-2 gap-6 items-center col-span-4">
          <template v-for="entry in detail" :key="entry.priority">
            <StartpageHeroElementTileDetailed :entry="entry" />
          </template>
        </div>
      </template>
      <template v-else>
        <div class="col-span-4">
          <NuxtCarousel v-bind="carouselSecondaryConfig">
            <NuxtSlide v-for="entry in detail" :key="entry.priority">
                <BasicBtErrorBoundary>
                  <StartpageHeroElementTileDetailed :entry="entry" />
                </BasicBtErrorBoundary>
              </NuxtSlide>
              <template #addons>
                <NuxtNavigation />
              </template>
          </NuxtCarousel>
        </div>
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import type { SliderEntry } from '../SliderStructure';

const props = defineProps<{
  entries: SliderEntry[];
}>();

// Derive entries during setup so SSR and hydration render the same layout.
const tiles = computed(() => {
  const simple = [];
  const detail = [];

  for (const entry of props.entries ?? []) {
    if (entry.type === 'Secondary' && entry.images.secondary) {
      detail.push({
        ...entry,
        image: entry.images.secondary.sm,
        altText: entry.altText ?? '',
      });
    } else if (entry.images.primary) {
      simple.push({
        ...entry,
        image: entry.images.primary.sm,
        altText: entry.altText ?? '',
      });
    }
  }

  return { simple, detail };
});

const simple = computed(() => tiles.value.simple);
const detail = computed(() => tiles.value.detail);

const carouselPrimaryConfig = {
  itemsToShow: 1,
  gap: 24,
  height: 500,
  wrapAround: true,
  autoplay: 3000,
  pauseAutoplayOnHover: true,
  snapAlign: 'center',
} as const;

const carouselSecondaryConfig = {
  itemsToShow: 2,
  gap: 24,
  height: 500,
  snapAlign: 'start',
  wrapAround: true,
} as const;
</script>

<style lang="postcss">
.hero-slide.triplet {
  & .carousel-ctrl-top-right {
    & button{
      @apply absolute top-6;
      &.carousel__prev {
        @apply left-auto right-10;
      }
      &.carousel__next {
        @apply left-auto right-6;
      }
    }
  }
  & .line-bullets {
    & .vueperslides__bullet {
      @apply mx-1;
      & .default {
        @apply !h-1 w-5 border border-secondary-dark shadow-none bg-secondary-dark;
      }
      &.vueperslides__bullet--active .default {
        @apply !h-1 w-10 border-2 border-primary shadow-none bg-primary;
      }
    }
  }
}
</style>
<style scoped lang="postcss">
.entry {
  @apply rounded-3xl overflow-hidden flex items-center;
}
</style>
